<template>
    <div>
        <b-button variant="primary" @click="openModal">
            Vermerk hinzufügen
        </b-button>

        <b-modal v-model="isModalOpen" hide-footer size="xl"
            title="Vermerk hinzufügen" @hide="reset()">
            <div class="record-note-create-modal loading-content-container">
                <div v-if="isLoading" class="loading-overlay d-flex justify-content-center pt-5">
                    <csp-loading-indicator/>
                </div>

                <csp-desktop-info-card
                    v-if="!isLoading"
                    :cols="12"
                    :form-context="formContext"
                    icon="inticon-users"
                    title="Kunde"
                    @cancel-form="reset"
                    @save="saveNewNote">
                    <h2>Grunddaten</h2>
                    <b-row>
                        <csp-desktop-info-card-element
                            class="col-3"
                            :form-context="formContext"
                            label="Thema"
                            :options-loader="loadNoteCodeCategoryOptions"
                            property="noteCodeCategory"
                            :required="true"
                            type="autocomplete"/>
                        <csp-desktop-info-card-element
                            class="col-9"
                            :dependencies="['noteCodeCategory']"
                            :form-context="formContext"
                            label="Vorgefertigte Vermerke"
                            :options-loader="loadNoteCodeOptions"
                            property="noteCode"
                            :required="true"
                            type="autocomplete"
                            @change="handleNoteCodeSelected"/>
                    </b-row>
                    <b-row>
                        <csp-desktop-info-card-element
                            class="col-9"
                            :form-context="formContext"
                            label="Fügen Sie hier einen eigenen Vermerk/Freitext ein"
                            property="note"
                            :required="!formContext.permissions.canChangeNoteCode"
                            type="autogrowtextarea"/>
                        <csp-desktop-info-card-element
                            class="col-3"
                            :form-context="formContext"
                            :is-loading-from-parent="isLoadingHideSettings > 0"
                            label="Nicht anzeigen für"
                            :mandatory-hide-for-group="mandatoryHideForGroup"
                            :options="hideForGroupOptionsAvailable"
                            property="hide"
                            :record-id="recordId"
                            type="hideForGroup"/>
                    </b-row>
                    <b-row>
                        <csp-desktop-info-card-element
                            class="col-3"
                            :form-context="formContext"
                            label="Vermerk erstellen als"
                            :options="formContext.complexFields.creatorId?.options"
                            property="creatorId"
                            type="autocomplete"/>
                        <csp-desktop-info-card-element
                            class="col-3 align-content-end"
                            :form-context="formContext"
                            :hide-label="true"
                            label="PRIO"
                            property="hasPriority"
                            type="checkbox"/>
                        <csp-desktop-info-card-element
                            v-if="!isAppointmentNoteCode"
                            class="col-3 align-content-end"
                            :form-context="formContext"
                            :hide-label="true"
                            label="Frist setzen"
                            property="hasDeadline"
                            type="checkbox"/>
                        <csp-desktop-info-card-element
                            v-if="!isAppointmentNoteCode"
                            class="col-3 align-content-end"
                            :form-context="formContext"
                            :hide-label="true"
                            label="Frist ist extern"
                            property="externalDeadline"
                            type="checkbox"/>
                        <csp-desktop-info-card-element
                            v-if="!isAppointmentNoteCode"
                            class="col-3"
                            :form-context="formContext"
                            :label="formContext.form.getProperty('hasDeadline') ? 'Datum Frist' : 'Datum Wiedervorlage'"
                            :max-date="null"
                            :min-date="dateNowAsString()"
                            property="dateReminder"
                            type="datepicker"/>
                        <csp-desktop-info-card-element
                            v-if="isAppointmentNoteCode"
                            class="col-3"
                            :form-context="formContext"
                            label="Termin"
                            :max-date="null"
                            :min-date="dateNowAsString()"
                            property="dateAppointment"
                            :required="true"
                            type="datepicker"/>
                        <csp-desktop-info-card-element
                            v-if="isAppointmentNoteCode"
                            class="col-1 align-content-end"
                            :form-context="formContext"
                            :hide-label="true"
                            label="Terminzeit"
                            property="timeAppointment"
                            :required="true"
                            step="60"
                            type="time"/>
                    </b-row>
                    <b-row>
                        <b-col cols="9"/>
                        <csp-desktop-info-card-element
                            v-if="formContext.form.getProperty('dateReminder')"
                            :buttons="true"
                            class="col-3 reminder-color-code"
                            :form-context="formContext"
                            label="Wiedervorlage Farbmarkierung"
                            property="reminderColorCode"
                            type="radio"/>
                    </b-row>
                    <hr>
                    <b-row>
                        <csp-desktop-info-card-element
                            class="col-6 add-recipient-select"
                            :form-context="formContext"
                            label="Empfänger hinzufügen"
                            :options="formContext.complexFields.noteRecipients.options"
                            property="noteRecipients"
                            :reset-after="true"
                            type="autocomplete"
                            @change="selectRecipient"/>
                    </b-row>
                    <div v-if="selectedRecipients.length > 0" class="note-recipients-container striped">
                        <h2 class="my-3">
                            Gewählte Empfänger
                        </h2>
                        <b-row v-for="selectedRecipient in selectedRecipients" :key="selectedRecipient.id"
                            :class="classesForRecipientRow(selectedRecipient)">
                            <b-col cols="1">
                                <b-button
                                    :disabled="!selectedRecipient.canBeDisabled"
                                    variant="secondary"
                                    @click="unselectRecipient(selectedRecipient)">
                                    X
                                </b-button>
                            </b-col>
                            <b-col cols="3">
                                {{ selectedRecipient.label }}
                            </b-col>
                            <b-col cols="3">
                                {{ selectedRecipient.groupName }}
                            </b-col>
                            <b-col cols="2">
                                <b-form-checkbox
                                    v-model="selectedRecipient.inquiry"
                                    :disabled="inquiryRecipient && inquiryRecipient.id !== selectedRecipient.id"
                                    @input="ensureInquiryRecipientNotHidden">
                                    <span>Antwort erforderlich</span>
                                </b-form-checkbox>
                            </b-col>
                            <b-col cols="3">
                                <b-form-checkbox
                                    v-model="selectedRecipient.emailNotification"
                                    :disabled="selectedRecipient.forceMailNotification">
                                    <span>E-Mail Benachrichtigung</span>
                                </b-form-checkbox>
                            </b-col>
                        </b-row>
                    </div>
                    <div v-if="formContext.data.firmRemarks">
                        <hr>
                        <h2>Hinweise</h2>
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <div v-html="formContext.data.firmRemarks"/>
                    </div>
                </csp-desktop-info-card>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import Container from '@services/Container';
    import '@desktop/scss/components/record/note-create-modal.scss';
    import FormDataMixin from '@desktop/components/mixins/FormDataMixin';
    import {dateInstanceAsString, dateNowAsString, formatDate, getDateInDays} from '@js/functions/date';
    import RoleGroupEnum from '@js/enums/RoleGroupEnum';

    const container = Container.getInstance();
    const recordRepository = container.getDesktopRecordRepository();

    export default {
        name: 'CostUnitCreateModal',
        mixins: [FormDataMixin],
        props: {
            createChildFor: {
                type: Number,
                default: 0,
            },
            hideForGroupOptions: {
                type: Array,
                default: null,
            },
            recordId: {
                type: Number,
                required: true,
            },
        },
        emits: ['created', 'reset'],
        data() {
            return {
                forcedRecipientNoBilling: null,
                forcedRecipientRequestTax: null,
                isModalOpen: false,
                isLoading: true,
                isLoadingHideSettings: 0,
                // Contains full recipient objects from the context data for the selected recipients
                selectedRecipients: [],
                // Contains the selected recipient options of/for the multiselect dropdown
                selectedRecipientOptions: [],
                // Role groups that are forced-hidden for the currently selected note code.
                mandatoryHideForGroup: [],
                // The user's own hide-for-group selection at the time forced groups were last applied.
                // Used to compute which forced groups were net-new so only those are removed on note code change.
                userHideValues: null,
            }
        },
        computed: {
            hideForGroupOptionsAvailable() {
                let options = this.hideForGroupOptions;

                // disable inquiry role group option for hide
                const inquiryRoleGroup = this.inquiryRecipient?.roleGroup;
                const effectiveInquiryHideGroup = this.getEffectiveHideRoleGroup(inquiryRoleGroup);
                // disable parent hide options
                const parentHide = this.formContext.data.parentHide

                options.forEach((option, index) => {
                    const isInquiryGroup = option.value === effectiveInquiryHideGroup;
                    const isParentHidden = parentHide && parentHide.includes(option.value);
                    if (isInquiryGroup || isParentHidden) {
                        // Disable the option of the inquiry recipient
                        options[index].disabled = true;
                    } else {
                        options[index].disabled = false;
                    }
                });

                return options;
            },
            hideForGroupOptionsEnabled() {
                let enabledHide = this.formContext.form.getProperty('hide');
                const parentHide = this.formContext.data.parentHide;
                if (parentHide) {
                    enabledHide = [...new Set([...enabledHide, ...parentHide])]
                }
                return enabledHide;
            },
            inquiryRecipient() {
                // Find the recipient that is marked as inquiry
                return this.selectedRecipients.find((recipient) => {
                    return recipient.inquiry;
                });
            },
            recipientsContextData() {
                return this.formContext.complexFields.noteRecipients.contextData;
            },
            recipientOptions() {
                return this.formContext.complexFields.noteRecipients.options;
            },
            isAppointmentNoteCode() {
                // Check if the selected note code category is for appointment notes
                return this.formContext.form.getProperty('noteCodeCategory') === 17;
            },
        },
        watch: {
            createChildFor: function() {
                if (this.createChildFor === 0) {
                    return;
                }
                this.openModal();
            },
            hideForGroupOptionsEnabled: function() {
                // Ensure that recipients that cannot see the note (because of hideForGroup) are disabled
                this.disableRecipientsFromHiddenRoleGroups();
            },
            selectedRecipients: function() {
                // Ensure that added recipeients are hidden if necessary, according to hide value
                this.disableRecipientsFromHiddenRoleGroups();
            },
        },
        methods: {
            classesForRecipientRow(selectedRecipient) {
                let classes = 'mb-2 p-2 align-items-center';
                if (selectedRecipient.disabled) {
                    classes += ' disabled';
                }
                return classes;
            },
            dateNowAsString,
            disableRecipientsFromHiddenRoleGroups() {
                this.selectedRecipients.forEach((recipient, index) => {
                    if (this.hideForGroupOptionsEnabled.includes(this.getEffectiveHideRoleGroup(recipient.roleGroup))) {
                        this.selectedRecipients[index].disabled = true;
                    } else {
                        this.selectedRecipients[index].disabled = false;
                    }
                });
            },
            ensureInquiryRecipientNotHidden() {
                if (!this.inquiryRecipient) {
                    // If no recipient is selected as inquiry, we do not need to enforce that his entity is not hidden.
                    return;
                }

                const effectiveHideGroup = this.getEffectiveHideRoleGroup(this.inquiryRecipient.roleGroup);

                let hide = this.formContext.form.getProperty('hide');

                hide = hide.filter((entry) => {
                    return entry !== effectiveHideGroup;
                });

                this.formContext.form.setProperty('hide', hide);
            },
            getEffectiveHideRoleGroup(roleGroup) {
                // Callcenter recipients have no own hide group — they follow the carDealer hide setting.
                if (roleGroup === RoleGroupEnum.CALLCENTER) {
                    return RoleGroupEnum.CAR_DEALER;
                }
                return roleGroup;
            },
            getResponsibleRecipientForGroup(group) {
                // Find the first recipient of the given group that is responsible
                return this.recipientsContextData.find((recipient) => {
                    return recipient.roleGroup === group && recipient.isResponsibleUser === true;
                });
            },
            handleCaseStillUpToDateSelected() {
                this.activateRecipientsNotifyByEmail();

                const reminderDate = getDateInDays(5);
                this.setReminderForDate(reminderDate);

                const noteText = '\nBitte wählen Sie in der Schadenakte eine der nachstehenden Optionen:\n\n' +
                    'Bearbeitung durch CSP / Kanzlei gewünscht\n' +
                    '→ unterschriebene Vollmacht hochladen\n' +
                    '→ Statuswechsel "Übergabe / Versand" durchführen\n\n' +
                    'Bearbeitung ausschließlich intern (bspw. Kaskoschaden)\n' +
                    '→ Statuswechsel "Eigenbearbeitung" durchführen\n\n' +
                    'Andernfalls (Fehleingabe, Doppelanlage, etc.)\n' +
                    '→ Statuswechsel "Abgeschlossen Autohaus" durchführen\n\n' +
                    'Für Rückfragen erreichen Sie mich gern unter Tel.: ' + this.formContext.data.currentUserPhone +
                    '\n\n' + 'Freundliche Grüße,\n' +
                    this.formContext.data.currentUserFullName;
                this.formContext.form.setProperty('note', noteText);

                this.activateInquiryForFirstRecipientOfGroup('carDealer')
            },
            handleInactiveNoteReminderSelected() {
                this.addHideForGroup('carDealer');
                this.activateRecipientsNotifyByEmail();
                const reminderDate = getDateInDays(5);
                this.setReminderForDate(reminderDate);
                const noteText = 'Der letzte Eintrag in dieser Schadenakte liegt einige Zeit zurück.' +
                    ' Bitte prüfen Sie, inwieweit hier Aktionen Ihrerseits erforderlich sind' +
                    ' bzw. neuere Informationen vorliegen, die nachgetragen werden sollten.' +
                    '\nVielen Dank für Ihre Rückinfo bis zum ' + formatDate(reminderDate) + '.';
                this.formContext.form.setProperty('note', noteText);

                if (this.activateInquiryForFirstRecipientOfGroup('lawOffice') === false) {
                    this.activateInquiryForFirstRecipientOfGroup('reseller');
                }
            },
            handleNoBillingNoteCodeSelected() {
                if (this.forcedRecipientNoBilling) {
                    // Already selected recipient for no billing
                    return;
                }
                // needs to be string!
                const recipientId = '17196';

                // Check if the recipient is already selected
                const isAlreadySelected = this.selectedRecipients.some((selectedRecipient) => {
                    return selectedRecipient.id === recipientId
                });

                // select Manuela Beck forcibly
                if (!isAlreadySelected) {
                    this.selectRecipientById(recipientId);
                }

                const recipientIndex = this.selectedRecipients.findIndex((recipient) => {
                    return recipient.id === recipientId;
                });

                // save "before force" state of the recipient to be able to reset it later
                this.forcedRecipientNoBilling = JSON.parse(JSON.stringify(this.selectedRecipients[recipientIndex]));
                this.forcedRecipientNoBilling.wasAlreadySelected = isAlreadySelected;

                this.selectedRecipients[recipientIndex].canBeDisabled = false;
                this.selectedRecipients[recipientIndex].emailNotification = true;
                this.selectedRecipients[recipientIndex].forceMailNotification = true;
            },
            handleNoteCodeSelected(value) {
                this.updateHideSettingsForNoteCode(value);

                if (value === 824) {
                    // Inaktive Schadenakte
                    this.handleInactiveNoteReminderSelected();
                }
                if (value === 825) {
                    // Ist der Fall noch aktuell?
                    this.handleCaseStillUpToDateSelected();
                }
                if (value === 908 || value === 909) {
                    // Keine Abrechnung
                    this.handleNoBillingNoteCodeSelected();
                } else if (this.forcedRecipientNoBilling) {
                    this.resetForcedRecipient(this.forcedRecipientNoBilling);
                    this.forcedRecipientNoBilling = null;
                }
                if (value === 1003) {
                    // SB und/oder MwSt. bei Kunde angefordert
                    this.handleRequestForTaxByServiceProviderSelected();
                } else if (this.forcedRecipientRequestTax) {
                    this.resetForcedRecipient(this.forcedRecipientRequestTax);
                    this.forcedRecipientRequestTax = null;
                }
            },
            handleRequestForTaxByServiceProviderSelected() {
                // Find the first recipient of the given group
                const recipient = this.getResponsibleRecipientForGroup('carDealer')

                if (!recipient) {
                    return;
                }

                // Check if the recipient is already selected
                const isAlreadySelected = this.selectedRecipients.some((selectedRecipient) => {
                    return selectedRecipient.id === recipient.id;
                });

                if (!isAlreadySelected) {
                    // Select the recipient
                    this.selectRecipientById(recipient.id);
                }

                const recipientIndex = this.selectedRecipients.findIndex((selectedRecipient) => {
                    return selectedRecipient.id === recipient.id;
                });

                // save "before force" state of the recipient to be able to reset it later
                this.forcedRecipientRequestTax = JSON.parse(JSON.stringify(this.selectedRecipients[recipientIndex]));
                this.forcedRecipientRequestTax.wasAlreadySelected = isAlreadySelected;

                this.selectedRecipients[recipientIndex].emailNotification = true;
                this.selectedRecipients[recipientIndex].forceMailNotification = true;
            },
            addHideForGroup(group) {
                let hide = this.formContext.form.getProperty('hide');
                // forceAdd cardealer to hideForGroup
                hide.push(group)
                // unify hide array to remove duplicates
                hide = Array.from(new Set(hide));
                this.formContext.form.setProperty('hide', hide);
            },
            activateInquiryForFirstRecipientOfGroup(roleGroup) {
                let recipientFound = false;
                this.selectedRecipients.forEach((recipient, index) => {
                    if (recipient.roleGroup === roleGroup) {
                        this.selectedRecipients[index].inquiry = true;
                        recipientFound = true;
                        return;
                    }
                });
                return recipientFound;
            },
            activateRecipientsNotifyByEmail() {
                this.selectedRecipients.forEach((recipient, index) => {
                    this.selectedRecipients[index].emailNotification = true;
                });
            },
            selectDefaultRecipients() {
                // Select default recipients from the context data
                this.recipientsContextData.forEach((recipient) => {
                    if (recipient.defaultEnabled) {
                        this.selectRecipientById(recipient.id);
                    }
                });
            },
            selectRecipient(recipientId) {
                if (!recipientId) {
                    return;
                }
                this.selectRecipientById(recipientId);
            },
            selectRecipientById(recipientId) {
                // Fetch contextData for selected recipient value
                const recipient = this.recipientsContextData.find((recipient) => {
                    if (recipient.id === recipientId) {
                        return recipient;
                    }
                });

                let selectRecipientOption = null;
                this.recipientOptions.forEach((recipientGroup) => {
                    recipientGroup.options.forEach((recipientOption) => {
                        if (recipientOption.value === recipientId) {
                            selectRecipientOption = recipientOption;
                        }
                    });
                });

                // Add selected recipient to the selectedRecipients array
                this.selectedRecipients.push(recipient);
                this.selectedRecipientOptions.push(selectRecipientOption);

                const dropdownOptions = this.formContext.complexFields.noteRecipients.options;
                // Find index of the option group from the selected option
                const selectedOptionGroupIndex = dropdownOptions.findIndex((optionGroup) => {
                    return optionGroup.optionGroup === recipient.groupName;
                });
                const selectedOptionGroup = dropdownOptions[selectedOptionGroupIndex];
                // remove seleted option from the option group options
                const filteredOptions = selectedOptionGroup.options.filter((option => {
                    return option.value !== recipientId;
                }));
                // apply updated options to the option group that the dropdown uses
                this.formContext.complexFields.noteRecipients.options[selectedOptionGroupIndex].options
                    = filteredOptions;
            },
            setReminderForDate(date) {
                // Expect date to be a Date object
                const dateString = dateInstanceAsString(date)
                this.formContext.form.setProperty('dateReminder', dateString);
            },
            unselectRecipient(unseletectedRecipient) {
                // Remove selected recipient from the selectedRecipients array
                this.selectedRecipients = this.selectedRecipients.filter((recipient) => {
                    return recipient.id !== unseletectedRecipient.id;
                });

                const recipientOption = this.selectedRecipientOptions.filter((option) => {
                    return option.value === unseletectedRecipient.id;
                });

                this.selectedRecipientOptions = this.selectedRecipientOptions.filter((option) => {
                    return option.value !== unseletectedRecipient.id;
                });

                const dropdownOptions = this.formContext.complexFields.noteRecipients.options;
                const selectedOptionGroupIndex = dropdownOptions.findIndex((optionGroup) => {
                    return optionGroup.optionGroup === unseletectedRecipient.groupName;
                });

                // Add the option back to the dropdown options
                this.formContext.complexFields.noteRecipients.options[selectedOptionGroupIndex].options.push(
                    recipientOption[0]
                );

                this.sortRecipientOptions();
            },
            loadNoteCodeCategoryOptions() {
                return recordRepository.loadLegacySelectOptions(
                    'noteCodeCategories',
                    {
                        vehicleDamageRecordId: this.recordId,
                    }
                );
            },
            loadNoteCodeOptions() {
                return recordRepository.loadLegacySelectOptions(
                    'noteCodes',
                    {
                        noteCodeCategoryId: this.formContext.form.getProperty('noteCodeCategory'),
                        vehicleDamageRecordId: this.recordId,
                    }
                );
            },
            loadForm() {
                this.isLoading = true;

                const loadFunction = () => {
                    return recordRepository.loadNoteCreationForm(this.recordId, this.createChildFor);
                }
                this.loadFormData(loadFunction).finally(() => {
                    this.isLoading = false;
                    this.sortRecipientOptions();
                    this.selectDefaultRecipients();
                });
            },
            openModal() {
                this.isModalOpen = true;
                this.loadForm();
            },
            reset() {
                this.formContext.form.reset();
                this.isModalOpen = false;
                this.isLoading = true,

                this.selectedRecipients = [];
                this.selectedRecipientOptions = [];
                this.forcedNoBillingRecipient = null;

                this.$emit('reset');
            },
            resetForcedRecipient(forcedRecipient) {
                const index = this.selectedRecipients.findIndex((recipient) => {
                    return recipient.id === forcedRecipient.id;
                });
                this.selectedRecipients[index].canBeDisabled = forcedRecipient.canBeDisabled;
                this.selectedRecipients[index].forceMailNotification = forcedRecipient.forceMailNotification;
                this.selectedRecipients[index].emailNotification = forcedRecipient.emailNotification;

                // If the recipient was not already selected before, we remove it from the selectedRecipients
                // Anyhow, we remove the wasAlreadySelected property
                if (!forcedRecipient.wasAlreadySelected) {
                    delete forcedRecipient.wasAlreadySelected;
                    this.unselectRecipient(forcedRecipient);
                } else {
                    delete forcedRecipient.wasAlreadySelected;
                }
            },
            sortRecipientOptions() {
                const options = this.formContext.complexFields.noteRecipients.options;
                options.forEach((optionGroup, index) => {
                    optionGroup.options.sort((a, b) => {
                        const orderDelta = a.order - b.order;
                        return orderDelta !== 0 ? orderDelta : a.text.localeCompare(b.text);
                    });
                    this.formContext.complexFields.noteRecipients.options[index].options = optionGroup.options;
                })
            },
            saveNewNote(formContext) {
                const saveFunction = (objectId, postData) => {
                    // add selected recipients information (incl. mail/inquiry) to the postData
                    postData.selectedRecipients = this.selectedRecipients;

                    return recordRepository.saveNoteNew(this.recordId, postData);
                }
                this.saveFormData(formContext, saveFunction, false).then(() => {
                    this.$emit('created');
                    this.$emit('reset');
                    this.reset();
                });
            },
            updateHideSettingsForNoteCode(noteCodeId) {
                this.isLoadingHideSettings++;
                this.selectedNoteCode = noteCodeId;

                if (!noteCodeId) {
                    this.updateForcedHideForGroup([]);
                    this.isLoadingHideSettings--;
                    return;
                }

                recordRepository.hideForGroupMandatoryForNote(
                    this.recordId,
                    {noteCodeId}
                ).then((response) => {
                    // Discard the response if the note code has changed since the request was launched.
                    if (this.selectedNoteCode !== noteCodeId) {
                        return;
                    }
                    this.updateForcedHideForGroup(response.data.attributes.mandatoryHideGroups);
                }).finally(() => {
                    this.isLoadingHideSettings--;
                });
            },
            // Removes previously forced groups, then applies the new forced groups on top.
            // Passing an empty array clears any previously forced groups.
            updateForcedHideForGroup(groups) {
                // Compute which of the previously forced groups were net-new additions (not already
                // checked by the user). Only those should be removed — groups the user had checked
                // independently must survive even if they were also in the previous forced set.
                const current = this.formContext.form.getProperty('hide') ?? [];
                const netForced = this.userHideValues !== null
                    ? this.mandatoryHideForGroup.filter((g) => !this.userHideValues.includes(g))
                    : this.mandatoryHideForGroup;
                const withoutNetForced = current.filter((g) => !netForced.includes(g));
                this.userHideValues = groups.length > 0 ? [...withoutNetForced] : null;
                this.mandatoryHideForGroup = groups;
                const merged = [...new Set([...withoutNetForced, ...groups])];
                this.formContext.form.setProperty('hide', merged);
            },
        },
    }
</script>
